/**
 * Admin → Platform Config → size modal: the "Virtual machine profile" block.
 * Infra (Azure VM) sizing, separate from the application sizing above it.
 * Sent as flat vm_* fields; a blank VM size means "no VM profile".
 */
import { useRef, useState } from 'react'
import styles from '@/pages/AdminPage.module.css'
import { useAzureCatalog } from '@/hooks/useAppriseAzure'
import {
  VM_DISK_TYPES, VM_IDENTITY_MODES, type VmSizingFormValues,
} from './vmSizingForm'
import type { AzureImage, VmSizing } from '@/types'
import { imageRefString } from './appriseCatalog'
import { OS_LABELS } from './appriseReview'
import { SearchableSelect, type ComboOption } from './LiveSelect'
import { checkAdminVmSize } from './appriseSizeCheck'

interface Props {
  values: VmSizingFormValues
  onChange: (patch: Partial<VmSizingFormValues>) => void
  /** The profile currently derived from the size's vCPU / RAM (shown when no explicit VM size is stored). */
  derived?: VmSizing
}

/** The reference region the VM size is checked in (the wizard's default region). */
const CHECK_REGION = { code: 'eastus2', label: 'East US 2' }

/**
 * Looks the typed VM size up in Azure (live): offered in the region? restricted? room in the vCPU quota? does the vCPU /
 * memory entered match Azure's? A warning never blocks saving: it tells the administrator what a request would hit.
 */
function VmSizeCheck({ values: v, onChange }: Pick<Props, 'values' | 'onChange'>) {
  const name = v.vm_size.trim()
  const sizes = useAzureCatalog('sizes', { location: CHECK_REGION.code, q: name }, name.length > 0)
  if (!name) return null
  if (sizes.isLoading) return <div className={styles.formHint} data-testid="vm-size-check">Checking Azure…</div>
  if (sizes.source === 'fallback') {
    return <div className={styles.formHint} data-testid="vm-size-check">Could not check this size against Azure right now.</div>
  }
  const size = sizes.items.find((s) => s.name.toLowerCase() === name.toLowerCase())
  const check = checkAdminVmSize(name, size, CHECK_REGION.label, { vcpu: v.vm_vcpu, memory: v.vm_memory_gb })
  if (!check) return null
  const differs = !!check.azure && (String(check.azure.vcpus) !== v.vm_vcpu.trim() || String(check.azure.memory_gb) !== v.vm_memory_gb.trim())
  return (
    <div data-testid="vm-size-check" role={check.tone === 'warn' ? 'alert' : 'status'}
      className={styles.formHint} style={{ color: check.tone === 'warn' ? 'var(--aptean-warning)' : undefined }}>
      {check.lines.map((line) => <div key={line}>{check.tone === 'warn' && line === check.lines[0] ? '⚠ ' : ''}{line}</div>)}
      {differs && check.azure && (
        <button type="button" className={styles.btnSecondary} style={{ marginTop: 6 }}
          onClick={() => onChange({ vm_vcpu: String(check.azure!.vcpus), vm_memory_gb: String(check.azure!.memory_gb) })}>
          Use Azure&apos;s vCPU / memory ({check.azure.vcpus} / {check.azure.memory_gb} GB)
        </button>
      )}
    </div>
  )
}

const NO_IMAGE: Partial<VmSizingFormValues> = {
  vm_image_publisher: '', vm_image_offer: '', vm_image_sku: '', vm_image_version: '', vm_image_label: '', vm_image_os_type: '',
}

/**
 * The size's default operating system, picked from the live Azure Marketplace list (the same list the wizard's Image box
 * searches). The wizard preselects it for this size; the requester can still choose another image.
 */
function DefaultImageField({ values: v, onChange }: Pick<Props, 'values' | 'onChange'>) {
  const [q, setQ] = useState('')
  const images = useAzureCatalog('images', { q }, true)
  // remember every image seen so the summary keeps its label while the admin searches
  const known = useRef(new Map<string, AzureImage>())
  for (const i of images.items) known.current.set(imageRefString(i), i)

  const chosen = !!(v.vm_image_publisher && v.vm_image_offer && v.vm_image_sku)
  const key = chosen ? imageRefString({ publisher: v.vm_image_publisher, offer: v.vm_image_offer, sku: v.vm_image_sku, version: v.vm_image_version || 'latest' }) : ''
  const options: ComboOption[] = images.items.map((i) => ({
    id: imageRefString(i),
    label: i.label,
    description: `${imageRefString(i)} · ${OS_LABELS[i.os_type] ?? i.os_type} · ${i.hyperv_generation.toUpperCase() === 'V2' ? 'Gen 2' : 'Gen 1'} · ${i.architecture}`,
    selected: imageRefString(i) === key,
  }))

  return (
    <div data-testid="vm-default-image">
      <SearchableSelect
        label="Operating system (default image)" full catalog={images} options={options}
        placeholder="Search Azure images (e.g. Windows Server 2022, Ubuntu)…"
        emptyText="No images match — try another search"
        onSearch={setQ}
        onSelect={(id) => {
          const img = known.current.get(id)
          if (img) onChange({
            vm_image_publisher: img.publisher, vm_image_offer: img.offer, vm_image_sku: img.sku,
            vm_image_version: img.version || 'latest', vm_image_label: img.label, vm_image_os_type: img.os_type,
          })
        }}
        summary={chosen ? (
          <>
            <span><strong>{v.vm_image_label || `${v.vm_image_offer}:${v.vm_image_sku}`}</strong></span>
            <span style={{ fontFamily: 'var(--aptean-font-mono)', fontSize: 12 }}>{key}</span>
            <span>Operating system: {OS_LABELS[v.vm_image_os_type as 'windows' | 'linux'] ?? v.vm_image_os_type}</span>
            <button type="button" className={styles.btnSecondary} style={{ alignSelf: 'flex-start' }} onClick={() => onChange(NO_IMAGE)}>
              Clear (use the portal default)
            </button>
          </>
        ) : (
          <span>None set: the wizard starts from its default image. Pick one to make it this size&apos;s default.</span>
        )}
      />
      <div className={styles.formHint}>Shown by default for this size in the New Environment wizard; requesters can change it there.</div>
    </div>
  )
}

export function AdminVmSizingBlock({ values: v, onChange, derived }: Props) {
  return (
    <>
      <div style={{ marginTop: 6, paddingTop: 12, borderTop: '1px solid var(--aptean-border-muted)', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--aptean-text-muted)' }}>
        Virtual machine profile <span className={styles.optionalHint} style={{ textTransform: 'none', letterSpacing: 0 }}>(Apprise sizes are defined by this profile only)</span>
      </div>
      <div className={styles.formGroup}>
        <label className={styles.formLabel}>User count <span className={styles.optionalHint}>(users this size supports)</span></label>
        <input className={styles.formInput} type="number" min={1} step={1} value={v.max_users} placeholder="e.g. 25"
          data-testid="vm-user-count" onChange={(e) => onChange({ max_users: e.target.value })} />
      </div>
      <DefaultImageField values={v} onChange={onChange} />
      {!v.vm_size.trim() && (
        <div className={styles.formHint} data-testid="vm-derived-hint">
          {derived
            ? `No VM size is set, so it is derived from the vCPU / RAM above: ${derived.vm_size} (${derived.vcpu} vCPU, ${derived.memory_gb} GB). Set one here to override it.`
            : 'No VM size is set. For Apprise it is derived from the vCPU / RAM above when they match a standard Azure size (for example 4 vCPU / 16 GB → Standard_D4s_v3); otherwise the size cannot be selected until a VM size is set here.'}
        </div>
      )}
      <div className={styles.formRow}>
        <div className={styles.formGroup}>
          <label className={styles.formLabel}>VM Size</label>
          <input className={styles.formInput} value={v.vm_size} placeholder="Standard_D2s_v3"
            onChange={(e) => onChange({ vm_size: e.target.value })} />
        </div>
        <div className={styles.formGroup}>
          <label className={styles.formLabel}>vCPU</label>
          <input className={styles.formInput} type="number" min={1} value={v.vm_vcpu} placeholder="2"
            onChange={(e) => onChange({ vm_vcpu: e.target.value })} />
        </div>
      </div>
      <VmSizeCheck values={v} onChange={onChange} />
      <div className={styles.formRow}>
        <div className={styles.formGroup}>
          <label className={styles.formLabel}>Memory (GB)</label>
          <input className={styles.formInput} type="number" min={0} step="0.5" value={v.vm_memory_gb} placeholder="8"
            onChange={(e) => onChange({ vm_memory_gb: e.target.value })} />
        </div>
        <div className={styles.formGroup}>
          <label className={styles.formLabel}>OS Disk (GB) <span className={styles.optionalHint}>(blank = image default)</span></label>
          <input className={styles.formInput} type="number" min={30} value={v.vm_os_disk_gb} placeholder="Image default"
            onChange={(e) => onChange({ vm_os_disk_gb: e.target.value })} />
        </div>
      </div>
      <div className={styles.formRow}>
        <div className={styles.formGroup}>
          <label className={styles.formLabel}>OS Disk Type</label>
          <select className={styles.formSelect} value={v.vm_os_disk_type} onChange={(e) => onChange({ vm_os_disk_type: e.target.value })}>
            {VM_DISK_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className={styles.formGroup}>
          <label className={styles.formLabel}>Default Identity</label>
          <select className={styles.formSelect} value={v.vm_identity_default} onChange={(e) => onChange({ vm_identity_default: e.target.value })}>
            {VM_IDENTITY_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
      </div>
      <label className={styles.formLabel} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
        <input type="checkbox" className={styles.checkbox} checked={v.vm_public_ip_default}
          onChange={(e) => onChange({ vm_public_ip_default: e.target.checked })} />
        Public IP on by default
      </label>
      <label className={styles.formLabel} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
        <input type="checkbox" className={styles.checkbox} checked={v.vm_monitoring_default}
          onChange={(e) => onChange({ vm_monitoring_default: e.target.checked })} />
        Monitoring on by default
      </label>
      <label className={styles.formLabel} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
        <input type="checkbox" className={styles.checkbox} checked={v.vm_backup_default}
          onChange={(e) => onChange({ vm_backup_default: e.target.checked })} />
        Backup on by default
      </label>
      <label className={styles.formLabel} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
        <input type="checkbox" className={styles.checkbox} checked={v.vm_overrides_allowed}
          onChange={(e) => onChange({ vm_overrides_allowed: e.target.checked })} />
        Allow administrators / deployers to override the VM profile
      </label>
    </>
  )
}
